import * as React from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import { toInputValue, subtractMonths } from '../utils/date';

export type PeriodPreset = 'lastDay' | 'last7Days' | 'lastMonth' | 'custom';

export type FilterBarProps = {
    loading: boolean;
    onApplyRange: (fromIsoInput: string, toIsoInput: string) => void;
};

export const FilterBar: React.FC<FilterBarProps> = React.memo(({ loading, onApplyRange }) => {
    const [preset, setPreset] = React.useState<PeriodPreset>('lastMonth');
    const [fromDate, setFromDate] = React.useState<string>(() => {
        const now = new Date();
        const from = subtractMonths(now, 1);
        return toInputValue(from);
    });
    const [toDate, setToDate] = React.useState<string>(() => toInputValue(new Date()));

    const handleRefresh = React.useCallback(() => {
        const now = new Date();
        if (preset === 'custom') {
            onApplyRange(fromDate, toDate);
            return;
        }
        let start = new Date(now);
        switch (preset) {
            case 'lastDay':
                // last 24 hours inclusive
                start = new Date(now.getTime() - (24 * 60 * 60 * 1000));
                break;
            case 'last7Days':
                // last 7 calendar days inclusive (including today): set start to 6 days ago at 00:00
                start = new Date(now);
                start.setHours(0, 0, 0, 0);
                start.setDate(start.getDate() - 6);
                // set end to today 23:59 for inclusive UI range
                const end7 = new Date(now);
                end7.setHours(23, 59, 59, 999);
                onApplyRange(toInputValue(start), toInputValue(end7));
                return;
            case 'lastMonth':
                start = subtractMonths(now, 1);
                break;
            default:
                break;
        }
        onApplyRange(toInputValue(start), toInputValue(now));
    }, [preset, fromDate, toDate, onApplyRange]);

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', p: 2, bgcolor: 'background.level1', borderRadius: '8px', mb: 3 }}>
            <FormControl size="sm" sx={{ minWidth: 180 }}>
                <FormLabel>Период</FormLabel>
                <Select
                    value={preset}
                    onChange={(_, value) => setPreset((value as PeriodPreset) ?? 'custom')}
                >
                    <Option value={'lastDay'}>Последний день</Option>
                    <Option value={'last7Days'}>Последние 7 дней</Option>
                    <Option value={'lastMonth'}>Последний месяц</Option>
                    <Option value={'custom'}>Произвольный</Option>
                </Select>
            </FormControl>
            {preset === 'custom' && (
                <>
                    <FormControl size="sm" sx={{ minWidth: 170 }}>
                        <FormLabel>От даты</FormLabel>
                        <Input type="datetime-local" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    </FormControl>
                    <FormControl size="sm" sx={{ minWidth: 170 }}>
                        <FormLabel>До даты</FormLabel>
                        <Input type="datetime-local" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                    </FormControl>
                </>
            )}
            <Button onClick={handleRefresh} loading={loading} sx={{ alignSelf: 'end' }}>Обновить</Button>
        </Box>
    );
});

FilterBar.displayName = 'FilterBar';


